import {
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { LocalhostOnlyBadge } from '@/components/LocalhostOnlyBadge';
import { isUnifyAdminShellRole } from '@/config/featureVisibility';
import { useRoleFlags } from '@/hooks/useRbac';
import {
  episodeOfCareTypeSchema,
  EMPTY_EOC_TYPE_FORM,
  type EpisodeOfCareTypeFormValues,
} from '@/schemas/episode-of-care-type.schema';
import { useAppointmentSessionTypes } from '../../calendar/appointmentTypeCatalog';
import {
  canActivateEpisodeOfCareType,
  slugifyEocTypeName,
  upsertEpisodeOfCareType,
  useEpisodeOfCareTypes,
  type EpisodeOfCareType,
} from '../../calendar/episodeOfCareTypeCatalog';

const INTAKE_TYPE_OPTIONS = [
  { value: 'both', label: 'Both' },
  { value: 'consultations', label: 'Consultations' },
  { value: 'intake-sessions', label: 'Intake Sessions' },
];

const SERVICE_COLUMNS: UnifyTableColumn<EpisodeOfCareType>[] = [
  { key: 'name', label: 'Name', getValue: (t) => t.name },
  {
    key: 'intakeType',
    label: 'Intake type',
    getValue: (t) => INTAKE_TYPE_OPTIONS.find((o) => o.value === t.intakeType)?.label ?? 'Both',
  },
  {
    key: 'active',
    label: 'Status',
    getValue: (t) => (t.active ? 'Active' : 'Inactive'),
    render: (t) => (
      <Badge variant="light" color={t.active ? 'green' : 'gray'} size="sm">
        {t.active ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
];

export function ServicesSettings(): JSX.Element {
  const medplum = useMedplum();
  const { isAdmin, role } = useRoleFlags();
  const { types, loading } = useEpisodeOfCareTypes();
  const sessionTypes = useAppointmentSessionTypes();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editId, setEditId] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const codeTouchedRef = useRef(false);

  const form = useForm<EpisodeOfCareTypeFormValues>({
    initialValues: EMPTY_EOC_TYPE_FORM,
    validate: zod4Resolver(episodeOfCareTypeSchema),
  });

  const sessionTypeOptions = useMemo(
    () =>
      sessionTypes
        .filter((t) => t.active)
        .map((t) => ({ value: t.id, label: t.description }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [sessionTypes],
  );

  /** Active options plus any currently-selected approved ids that are no longer
   * active — preserved (labeled "(inactive)") until the admin removes them. */
  const approvedSessionTypeOptions = useMemo(() => {
    const options = [...sessionTypeOptions];
    for (const id of form.values.approvedSessionTypeIds) {
      if (options.some((o) => o.value === id)) continue;
      const known = sessionTypes.find((t) => t.id === id);
      options.push({ value: id, label: `${known?.description ?? id} (inactive)` });
    }
    return options;
  }, [sessionTypeOptions, sessionTypes, form.values.approvedSessionTypeIds]);

  const sortedTypes = useMemo(
    () => [...types].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [types],
  );

  const isEdit = editId !== undefined;

  const activationBlocked =
    form.values.active &&
    !canActivateEpisodeOfCareType(form.values.intakeType, {
      intake: form.values.intakeSessionTypeId,
      consultation: form.values.consultationSessionTypeId,
      standard: form.values.standardSessionTypeId,
    });

  const canSubmit = form.isValid() && !activationBlocked;

  // Auto-derive code from name on create when user hasn't manually edited the code field.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isEdit && !codeTouchedRef.current) {
      form.setFieldValue('code', slugifyEocTypeName(form.values.name));
    }
  }, [form.values.name]); // intentionally omit form/isEdit — only re-run on name change

  function openAddModal(): void {
    setEditId(undefined);
    codeTouchedRef.current = false;
    form.reset();
    openModal();
  }

  function openEditModal(type: EpisodeOfCareType): void {
    setEditId(type.id);
    codeTouchedRef.current = true;
    form.setValues({
      name: type.name,
      code: type.code,
      intakeType: type.intakeType,
      intakeSessionTypeId: type.intakeSessionTypeId,
      consultationSessionTypeId: type.consultationSessionTypeId,
      standardSessionTypeId: type.standardSessionTypeId,
      approvedSessionTypeIds: type.approvedSessionTypeIds,
      active: type.active,
      soharSpecialtyCode: type.soharSpecialtyCode,
    });
    form.clearErrors();
    openModal();
  }

  const handleModalSubmit = form.onSubmit(async (rawValues) => {
    if (activationBlocked) return;
    setSaving(true);
    try {
      const parsed = episodeOfCareTypeSchema.parse(rawValues);
      await upsertEpisodeOfCareType(medplum, {
        id: editId,
        name: parsed.name,
        code: parsed.code,
        active: parsed.active,
        intakeType: parsed.intakeType,
        sessionMap: {
          intake: parsed.intakeSessionTypeId,
          consultation: parsed.consultationSessionTypeId,
          standard: parsed.standardSessionTypeId,
        },
        soharSpecialtyCode: parsed.soharSpecialtyCode,
        approvedSessionIds: parsed.approvedSessionTypeIds,
      });
      notifications.show({
        title: isEdit ? 'Service updated' : 'Service created',
        message: parsed.name,
        color: 'green',
      });
      closeModal();
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  });

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-end">
          <div>
            <Group gap="sm" align="center" wrap="wrap" mb="xs">
              <Title order={2} mb={0}>
                EOC Types
              </Title>
              {isUnifyAdminShellRole(role) ? null : <LocalhostOnlyBadge />}
            </Group>
            <Text size="sm" c="dimmed">
              Configure episode-of-care service types and clinical session preferences
            </Text>
          </div>
          {isAdmin && (
            <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
              Add Episode of Care Type
            </Button>
          )}
        </Group>

        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : (
          <UnifyTable
            columns={SERVICE_COLUMNS}
            data={sortedTypes}
            getRowKey={(t) => t.id}
            onRowClick={isAdmin ? openEditModal : undefined}
          />
        )}
      </Stack>

      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={isEdit ? 'Edit Episode of Care Type' : 'Add Episode of Care Type'}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="e.g. Ketamine-Assisted Therapy"
            required
            {...form.getInputProps('name')}
          />
          <TextInput
            label="Code (slug)"
            description={
              isEdit
                ? 'Immutable after creation — referenced by EpisodeOfCare records.'
                : 'Auto-derived from name; editable before first save.'
            }
            value={form.values.code}
            onChange={(e) => {
              codeTouchedRef.current = true;
              form.setFieldValue('code', e.currentTarget.value);
            }}
            readOnly={isEdit}
            error={!isEdit ? form.errors.code : undefined}
            required={!isEdit}
          />
          <TextInput
            label="Sohar specialty code"
            description="Three letters; saved in uppercase."
            maxLength={3}
            required
            value={form.values.soharSpecialtyCode}
            onChange={(e) => {
              const filtered = e.currentTarget.value.replace(/[^A-Za-z]/g, '').slice(0, 3);
              form.setFieldValue('soharSpecialtyCode', filtered);
            }}
            error={form.errors.soharSpecialtyCode}
          />
          <Select
            label="Intake type"
            data={INTAKE_TYPE_OPTIONS}
            value={form.values.intakeType}
            onChange={(v) => form.setFieldValue('intakeType', (v ?? 'both') as EpisodeOfCareTypeFormValues['intakeType'])}
            allowDeselect={false}
          />
          <Select
            label="Intake session type"
            data={sessionTypeOptions}
            value={form.values.intakeSessionTypeId}
            onChange={(v) => form.setFieldValue('intakeSessionTypeId', v)}
            clearable
            searchable
            disabled={form.values.intakeType === 'consultations'}
          />
          <Select
            label="Consultation session type"
            data={sessionTypeOptions}
            value={form.values.consultationSessionTypeId}
            onChange={(v) => form.setFieldValue('consultationSessionTypeId', v)}
            clearable
            searchable
            disabled={form.values.intakeType === 'intake-sessions'}
          />
          <Select
            label="Standard session type"
            data={sessionTypeOptions}
            value={form.values.standardSessionTypeId}
            onChange={(v) => form.setFieldValue('standardSessionTypeId', v)}
            clearable
            searchable
          />
          <MultiSelect
            label="Approved session types"
            description="The intake, consultation, and standard session types above are always approved. Add extra schedulable types here (e.g. shorter-session or special-modality CPT variants)."
            data={approvedSessionTypeOptions}
            value={form.values.approvedSessionTypeIds}
            onChange={(v) => form.setFieldValue('approvedSessionTypeIds', v)}
            clearable
            searchable
          />
          {isEdit && (
            <>
              <Switch
                label="Active"
                {...form.getInputProps('active', { type: 'checkbox' })}
              />
              {activationBlocked && (
                <Text size="sm" c="red">
                  Set all session types for this intake type before activating.
                </Text>
              )}
            </>
          )}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={() => void handleModalSubmit()} loading={saving} disabled={!canSubmit}>
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
